import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormCaptureFields, FormField } from '@/components/vsleads/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { GripVertical, MessageCircle, Hash } from 'lucide-react';
import { getFieldLabel, getFieldPlaceholder } from '@/utils/vsleads/getDefaultConfig';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface FieldsTabProps {
  form: UseFormReturn<FormCaptureFields>;
}

const AVAILABLE_FIELDS: Array<FormField['id']> = ['firstName', 'lastName', 'email', 'whatsapp'];

function getFieldIcon(fieldId: FormField['id']) {
  switch (fieldId) {
    case 'whatsapp':
      return <MessageCircle className="h-[18px] w-[18px] text-[#6B7280]" />;
    case 'lastName':
      return <Hash className="h-[18px] w-[18px] text-[#6B7280]" />;
    default:
      return null;
  }
}

function SortableFieldItem({
  field,
  onUpdate,
  onRemove,
}: {
  field: FormField;
  onUpdate: (updates: Partial<FormField>) => void;
  onRemove: () => void;
}) {
  useLanguage();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-3 p-4 bg-card border rounded-lg"
    >
      <button
        type="button"
        className="mt-2 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-5 w-5" />
      </button>
      
      <div className="flex-1 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">{getFieldLabel(field.id)}</Label>
          <div className="flex items-center gap-2">
            <Switch
              checked={field.required}
              onCheckedChange={(checked) => onUpdate({ required: checked })}
            />
            <Label className="text-xs">{translateInterfaceText("Obrigatório")}</Label>
          </div>
        </div>
        
        <div>
          <Label className="text-xs text-muted-foreground">{translateInterfaceText("Placeholder")}</Label>
          <Input
            value={field.placeholder}
            onChange={(e) => onUpdate({ placeholder: e.target.value })}
            placeholder={getFieldPlaceholder(field.id)}
            className="mt-1"
          />
        </div>
      </div>

      <button
        type="button"
        onClick={onRemove}
        className="text-destructive hover:text-destructive/80 text-xs"
      >
        {translateInterfaceText("Remover")}</button>
    </div>
  );
}

export function FieldsTab({ form }: FieldsTabProps) {
  useLanguage();
  const fields = form.watch('fields') || [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex((f) => f.id === active.id);
      const newIndex = fields.findIndex((f) => f.id === over.id);
      const newFields = arrayMove(fields, oldIndex, newIndex).map((f, idx) => ({
        ...f,
        order: idx,
      }));
      form.setValue('fields', newFields, { shouldDirty: true });
    }
  };

  const addField = (fieldId: FormField['id']) => {
    const newField: FormField = {
      id: fieldId,
      required: false,
      placeholder: getFieldPlaceholder(fieldId),
      order: fields.length,
    };
    form.setValue('fields', [...fields, newField], { shouldDirty: true });
  };

  const updateField = (index: number, updates: Partial<FormField>) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...updates };
    form.setValue('fields', newFields, { shouldDirty: true });
  };

  const removeField = (index: number) => {
    const newFields = fields.filter((_, i) => i !== index);
    form.setValue('fields', newFields, { shouldDirty: true });
  };

  const selectedFieldIds = fields.map((f) => f.id);
  const availableToAdd = AVAILABLE_FIELDS.filter((id) => !selectedFieldIds.includes(id));

  return (
    <div className="space-y-6">
      <Card className="bg-white border border-[#E5E7EB] rounded-xl shadow-[0_2px_6px_rgba(0,0,0,0.04)]">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-[#111827]">
            {translateInterfaceText("Adicionar novos campos")}</CardTitle>
          <CardDescription className="text-sm text-[#6B7280]">
            {translateInterfaceText("Expanda seu formulário com informações complementares")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {availableToAdd.map((fieldId) => {
              const icon = getFieldIcon(fieldId);
              return (
                <button
                  key={fieldId}
                  type="button"
                  onClick={() => addField(fieldId)}
                  className="group flex items-center gap-3 h-14 px-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-[10px] transition-all duration-150 hover:bg-[#F3F4F6] hover:border-[#2D72FF] hover:shadow-[0_2px_6px_rgba(0,0,0,0.06)] active:bg-[#EBF3FF] active:border-[#2D72FF] cursor-pointer"
                >
                  {icon && (
                    <div className="flex-shrink-0">
                      {icon}
                    </div>
                  )}
                  <span className="text-[15px] font-semibold text-[#111827]">
                    {getFieldLabel(fieldId)}
                  </span>
                </button>
              );
            })}
          </div>
          {availableToAdd.length === 0 && (
            <p className="text-sm text-[#6B7280] text-center py-4">
              {translateInterfaceText("Todos os campos disponíveis foram adicionados")}</p>
          )}
        </CardContent>
      </Card>

      {fields.length > 0 && (
        <Card className="bg-white border border-[#E5E7EB] rounded-xl shadow-[0_2px_6px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-base">{translateInterfaceText("Campos Selecionados")}</CardTitle>
            <CardDescription>
              {translateInterfaceText("Arraste para reordenar, configure obrigatoriedade e placeholders")}</CardDescription>
          </CardHeader>
          <CardContent>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                  {fields.map((field, index) => (
                    <SortableFieldItem
                      key={field.id}
                      field={field}
                      onUpdate={(updates) => updateField(index, updates)}
                      onRemove={() => removeField(index)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
